/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { TenderType } from '@msdyn365-commerce/retail-proxy';
import * as React from 'react';

/**
 * Distributor Payment type list interface.
 */
export interface IDistributorPaymentTypesListProps {
    mainClassName: string;
    paymentTypesMenuText?: string;
    paymentTypesList?: TenderType[];
    defaultOptionText?: string;
    onChange(paymentType: string): Promise<void>;
}

/**
 * Distributor Payment types state interface.
 */
export interface IDistributorPaymentTypeListState {
    isFilterExpanded: boolean;
    selectedPaymentTypes?: string;
}

/**
 * Distributor payment option list Filter
 */
export class DistributorPaymentOptionList extends React.PureComponent<IDistributorPaymentTypesListProps, IDistributorPaymentTypeListState> {
    private readonly _distributorPaymentTypesMenu: React.RefObject<HTMLUListElement>;

    public constructor(props: IDistributorPaymentTypesListProps) {
        super(props);
        this._distributorPaymentTypesMenu = React.createRef();
        this.state = {
            isFilterExpanded: false,
            selectedPaymentTypes: ''
        };
    }

    public render(): JSX.Element {
        const { mainClassName, paymentTypesMenuText, paymentTypesList, defaultOptionText } = this.props;
        const selectedPaymentTypesDesc = defaultOptionText
            ? defaultOptionText
            : paymentTypesList?.find((payment: TenderType) => payment.TenderTypeId === this.state.selectedPaymentTypes)?.Name;
        const role = 'menu';
        return (
            <div className={`${mainClassName}__search-header-payment`}>
                <button className={`${mainClassName}__search-header-payment-menu`} onClick={this._renderTenderTypes}>
                    {selectedPaymentTypesDesc !== undefined ? selectedPaymentTypesDesc : paymentTypesMenuText}
                    <span className={`${mainClassName}__search-header-payment-span`} />
                </button>
                {this.state.isFilterExpanded ? (
                    <ul className={`${mainClassName}__search-header-payment-list`} role={role} ref={this._distributorPaymentTypesMenu}>
                        {paymentTypesList?.map((tenderType: TenderType) => {
                            return (
                                <li
                                    className={`${mainClassName}__search-header-payment-list-item`}
                                    key={tenderType.TenderTypeId}
                                    role='presentation'
                                >
                                    <a
                                        key={tenderType.TenderTypeId}
                                        data-value={tenderType.TenderTypeId}
                                        role='menuitem'
                                        className={`${mainClassName}__search-header-payment-list-link`}
                                        tabIndex={0}
                                        aria-label={tenderType.Name}
                                        onClick={this.handleFilterSelection}
                                        onKeyDown={this.handleFilterSelection}
                                    >
                                        <span className={`${mainClassName}__search-header-payment-list-item__text`}>{tenderType.Name}</span>
                                    </a>
                                </li>
                            );
                        })}
                    </ul>
                ) : null}
            </div>
        );
    }

    /**
     * Method to call when drop down get selected.
     */
    private readonly _renderTenderTypes = () => {
        const isExpandOptionsState = this.state.isFilterExpanded;
        this.setState({
            isFilterExpanded: !isExpandOptionsState
        });
    };

    private readonly handleFilterSelection = async (event: React.SyntheticEvent<HTMLAnchorElement>): Promise<void> => {
        event.preventDefault();
        const paymentCode = event.currentTarget.getAttribute('data-value');
        if (paymentCode !== null) {
            this.setState({
                selectedPaymentTypes: paymentCode
            });

            this._renderTenderTypes();
            await this.props.onChange(paymentCode);
        }
    };
}
